import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { api } from "@/lib/api";
import { ArrowRight, ShieldCheck } from "lucide-react";
import PublicFooter from "@/components/public/PublicFooter";
import LifestyleVideo from "@/components/public/LifestyleVideo";
import FounderStory from "@/components/public/FounderStory";
import MiniStories from "@/components/public/MiniStories";
import ChooseYourVault from "@/components/public/ChooseYourVault";
import { getMoment } from "@/data/lifestyle_moments";
import LangCurrencySelector from "@/components/public/LangCurrencySelector";

/* ═════════════════════════════════════════════════════════════════════
   VAULT — Public Landing (v2, minimal)
   Strictly 5 sections. 80% dark navy · 15% warm ivory · 5% muted gold.
   1) Hero (headline + 4-KPI preview)
   2) Product statement + one large dashboard mock
   3) Three core benefits
   4) Security (4 points)
   5) Final CTA
   Pricing lives on its own page at /pricing.
   ═════════════════════════════════════════════════════════════════════ */

const NAV = [
    { label: "Product",   href: "/product"  },
    { label: "Together",  href: "/together" },
    { label: "Solutions", href: "/solutions/individuals" },
    { label: "Learn",     href: "/learn"    },
    { label: "Security",  href: "/security" },
    { label: "Pricing",   href: "/pricing"  },
    { label: "Demo",      href: "/demo"     },
];

const BENEFITS = [
    { title: "Control Your Money",   body: "Cards, expenses, payments and subscriptions." },
    { title: "Understand Your Wealth", body: "Assets, investments, liabilities and net worth." },
    { title: "Stay Ahead",           body: "Reminders, renewals, alerts and financial insights." },
];

const SECURITY = [
    "Role-based access",
    "Masked card information",
    "No CVV, PIN or OTP storage",
    "Secure backups and audit history",
];

export default function Landing() {
    const { user } = useAuth();
    const { t } = useLocale();
    const nav = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [testimonials, setTestimonials] = useState([]);
    const [testimonialsNote, setTestimonialsNote] = useState("");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        // New spec-driven homepage feed — randomised across page loads and
        // returns display-format-redacted names. Falls back to the legacy
        // endpoint if the new one is empty (backward compat).
        api.get("/public/testimonials/homepage", { params: { limit: 3 } })
            .then((r) => {
                setTestimonials(r.data?.testimonials || []);
                setTestimonialsNote(r.data?.note || "");
            })
            .catch(() => api.get("/public/testimonials")
                            .then(r => setTestimonials(r.data?.testimonials || []))
                            .catch(() => setTestimonials([])));
    }, []);

    // ─── UTM / referrer attribution (fires once per landing) ───────
    // Reads utm_* params + document.referrer and POSTs to
    // /api/growth/attribution so the Command Centre can see which
    // channels bring in trials + paying customers. Deduped by
    // sessionStorage so bouncing between hero → pricing → hero doesn't
    // create 3 attribution rows for the same visit.
    useEffect(() => {
        try {
            if (sessionStorage.getItem("vault.attribution_sent") === "1") return;
        } catch { /* noop */ }
        const sp = new URLSearchParams(window.location.search);
        const payload = {
            utm_source:   sp.get("utm_source") || undefined,
            utm_medium:   sp.get("utm_medium") || undefined,
            utm_campaign: sp.get("utm_campaign") || undefined,
            utm_content:  sp.get("utm_content") || undefined,
            utm_term:     sp.get("utm_term") || undefined,
            referrer:     document.referrer || undefined,
        };
        // Skip if nothing to send AND referrer is empty AND same-origin
        // (i.e. an in-app navigation, not a real landing).
        const hasSignal = Object.values(payload).some(Boolean);
        if (!hasSignal) return;
        api.post("/growth/attribution", payload)
            .then(() => {
                try { sessionStorage.setItem("vault.attribution_sent", "1"); } catch { /* noop */ }
            })
            .catch(() => { /* silent — analytics must never break landing */ });
    }, []);

    // Note: signed-in visitors are welcomed on `/` — they now see the
    // marketing landing but the top-right CTA switches to "Open Dashboard"
    // so they can jump back to the app anytime. Public marketing routes
    // (pricing, security, etc.) remain accessible even while authenticated.

    return (
        <div className="v-landing" data-testid="landing-page">
            <style>{landingCSS}</style>

            {/* ═══════ NAV ═══════ */}
            <header className={`v-nav ${scrolled ? "v-nav--scrolled" : ""}`}>
                <div className="v-container flex items-center justify-between py-5">
                    <Link to="/" className="flex items-baseline gap-2" data-testid="landing-brand">
                        <span className="v-brand-word">VAULT</span>
                        <span className="v-brand-tag">by PD</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8">
                        {NAV.map((l) => (
                            <Link key={l.href} to={l.href} className="v-nav-link">{l.label}</Link>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <LangCurrencySelector />
                        {user ? (
                            <Link to="/dashboard" className="v-btn v-btn--gold" data-testid="nav-open-dashboard">Open Dashboard</Link>
                        ) : (
                            <>
                                <Link to="/login" className="hidden sm:inline v-nav-link" data-testid="nav-signin">Sign in</Link>
                                <Link to="/signup" className="v-btn v-btn--primary" data-testid="nav-get-started">Start Free Trial</Link>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* ═══════ 1. HERO ═══════ */}
            <section className="v-container v-hero">
                <div className="grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-24 items-center">
                    <div>
                        <div className="v-hero-eyebrow" data-testid="hero-eyebrow">
                            The Personal Financial Operating System
                        </div>
                        <h1 className="v-hero-headline" data-testid="hero-headline">
                            Your financial life,<br />
                            <span className="v-gold">organised in one secure VAULT.</span>
                        </h1>
                        <p className="v-hero-sub">
                            Cards, subscriptions, documents, investments, assets, liabilities and net
                            worth — brought together in one organised financial workspace. VAULT helps
                            you see what you have, what needs attention and what comes next.
                        </p>
                        <p className="v-hero-source" data-testid="hero-data-source">
                            You decide what goes into VAULT — nothing connects or pulls financial data
                            automatically unless you choose to enable a supported connection.
                        </p>
                        <div className="v-hero-ctas">
                            <Link
                                to="/tools/financial-organisation-checkup"
                                className="v-btn v-btn--gold v-btn--lg"
                                data-testid="hero-checkup-cta"
                            >
                                Take the 2-Minute Check-up <ArrowRight size={16} />
                            </Link>
                            <Link to="/signup" className="v-btn v-btn--ghost v-btn--lg" data-testid="hero-get-started">
                                Start Free Trial
                            </Link>
                        </div>
                        <div className="v-hero-trial" data-testid="hero-trial-line">
                            14-day free trial · no card required ·{" "}
                            <Link to="/demo" className="v-hero-demo-link" data-testid="hero-demo">
                                Explore Demo
                            </Link>
                        </div>
                        <div className="v-hero-price" data-testid="hero-pricing-teaser">
                            <Link to="/pricing" className="v-hero-price-link">View pricing plans →</Link>
                        </div>
                    </div>

                    <div id="preview" className="v-preview">
                        <div className="v-preview-head">
                            <div className="v-preview-eyebrow">Command Centre</div>
                            <div className="v-preview-title">Snapshot · Today</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-5">
                            <PreviewKpi label="Net worth"           value="₹1.33 Cr"  delta="+8.4%"  positive />
                            <PreviewKpi label="Monthly spending"    value="₹4.15 L"   delta="-3.1%"  positive />
                            <PreviewKpi label="Upcoming payments"   value="₹42,850"   delta="3 days" />
                            <PreviewKpi label="Credit utilisation"  value="3.3%"      delta="Healthy" positive />
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════ 1.5 HUMAN LIFESTYLE MOMENT (individual) ═══════ */}
            <LifestyleVideo moment={getMoment("homepage")} side="right" />

            {/* ═══════ 1.6 FOUNDER STORY ═══════ */}
            <FounderStory />

            {/* ═══════ 1.7 MINI STORIES RAIL ═══════ */}
            <MiniStories />

            {/* ═══════ 1.8 CHOOSE YOUR VAULT ═══════ */}
            <ChooseYourVault />

            {/* ═══════ 2. PRODUCT STATEMENT + DASHBOARD ═══════ */}
            <section className="v-statement">
                <div className="v-container text-center">
                    <h2 className="v-statement-line">
                        {t("statement.line_1")}<br />
                        {t("statement.line_2")}<br />
                        <span className="v-gold">{t("statement.line_3")}</span>
                    </h2>
                </div>
                <div className="v-container mt-16 md:mt-20">
                    <DashboardCanvas />
                </div>
            </section>

            {/* ═══════ 3. THREE BENEFITS ═══════ */}
            <section className="v-container v-benefits">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                    {[
                        { key: "control", title: t("benefit.control"), body: t("benefit.control_body") },
                        { key: "wealth",  title: t("benefit.wealth"),  body: t("benefit.wealth_body") },
                        { key: "ahead",   title: t("benefit.ahead"),   body: t("benefit.ahead_body") },
                    ].map((b) => (
                        <div key={b.key} className="v-benefit" data-testid={`benefit-${b.key}`}>
                            <div className="v-benefit-dot" />
                            <h3 className="v-benefit-title">{b.title}</h3>
                            <p className="v-benefit-body">{b.body}</p>
                        </div>
                    ))}
                </div>

                {/* "What VAULT Users Are Saying" — premium, understated, only
                    verified approved consented customers. Randomised display
                    order courtesy of /public/testimonials/homepage. */}
                {testimonials.length >= 3 ? (
                    <TestimonialSection testimonials={testimonials} note={testimonialsNote} />
                ) : (
                    <div className="v-beta" data-testid="launch-cta">
                        <div className="v-beta-grid">
                            <div className="v-beta-copy">
                                <div className="v-beta-eyebrow">
                                    <span className="v-beta-dot" />
                                    THE PERSONAL FINANCIAL OPERATING SYSTEM
                                </div>
                                <h3 className="v-beta-title">Your brain has better things to remember.</h3>
                                <p className="v-beta-body">
                                    Due dates. Renewals. Subscriptions. Cards. Documents. VAULT can
                                    keep the financial details organised — so you don&rsquo;t have to
                                    carry them all in your head.
                                </p>
                                <ul className="v-beta-perks">
                                    <li><span className="v-beta-check" /> 14-day free trial · no card required</li>
                                    <li><span className="v-beta-check" /> Cancel any time from Settings</li>
                                    <li><span className="v-beta-check" /> Explore the demo before you sign up</li>
                                </ul>
                            </div>
                            <div className="v-beta-cta-col">
                                <Link
                                    to="/tools/financial-organisation-checkup"
                                    className="v-beta-btn"
                                    data-testid="launch-cta-primary"
                                >
                                    Take the 2-Minute Check-up
                                    <ArrowRight size={16} />
                                </Link>
                                <Link
                                    to="/signup"
                                    className="v-beta-btn v-beta-btn--ghost"
                                    data-testid="launch-cta-secondary"
                                >
                                    Start Free Trial
                                </Link>
                                <div className="v-beta-fine">
                                    Already a customer? <Link to="/login" className="underline">Sign in</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* ═══════ 4. SECURITY ═══════ */}
            <section id="security" className="v-container v-security">
                <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
                    <div>
                        <div className="v-security-badge">
                            <ShieldCheck size={13} /> SECURITY &amp; PRIVACY
                        </div>
                        <h2 className="v-security-headline">{t("security.headline")}</h2>
                    </div>
                    <ul className="v-security-list">
                        {SECURITY.map((s) => (
                            <li key={s} className="v-security-item">
                                <span className="v-security-check" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ═══════ 6.5 "I WANT TO…" JOURNEYS ═══════ */}
            <section className="v-wants" data-testid="landing-wants">
                <div className="v-container">
                    <div className="v-wants__eyebrow">WHERE DO YOU WANT TO START?</div>
                    <h2 className="v-wants__headline">I want to&hellip;</h2>
                    <p className="v-wants__sub">
                        Eight real problems. Pick the one you&rsquo;d actually solve tonight.
                    </p>
                    <div className="v-wants__grid">
                        {[
                            { title: "Know what's due",                    body: "Every payment, renewal and obligation, in one place, before it's late.",                          to: "/learn/articles/information-problem",     tone: "cobalt"  },
                            { title: "Stop forgetting subscriptions",       body: "Especially the annual ones you'll otherwise pay for twice.",                                     to: "/learn/articles/subscriptions",            tone: "coral"   },
                            { title: "Understand my net worth",             body: "What you own, what you owe, and where that actually leaves you.",                                to: "/learn/articles/bank-balance-vs-net-worth", tone: "emerald" },
                            { title: "Organise important documents",        body: "The important things your family should be able to find in under a minute.",                     to: "/learn/articles/family-vault-checklist",  tone: "teal"    },
                            { title: "See what I own and owe",              body: "Assets and liabilities side by side, with the real number underneath.",                          to: "/learn/articles/bank-balance-vs-net-worth", tone: "amber"   },
                            { title: "Organise finances with my partner",   body: "Shared where it matters. Private where it counts.",                                              to: "/together",                                tone: "violet"  },
                            { title: "Get a clearer monthly picture",       body: "Where the money went and what's left. One page.",                                                to: "/tools/financial-organisation-checkup",    tone: "gold"    },
                            { title: "Separate personal and business",      body: "Two clean financial worlds. Never accidentally mixed.",                                          to: "/solutions/business-owners",             tone: "aqua"    },
                        ].map((c, i) => (
                            <Link key={i} to={c.to} className={`v-wants__card v-wants__card--${c.tone}`} data-testid={`landing-want-${i}`}>
                                <div className="v-wants__title">{c.title}</div>
                                <p className="v-wants__body">{c.body}</p>
                                <span className="v-wants__arrow">Show me <ArrowRight size={12}/></span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════ 7. USEFUL TOOLS (2 MIN VALUE) ═══════ */}
            <section className="v-tools" data-testid="landing-tools">
                <div className="v-container">
                    <div className="v-tools__eyebrow">DO SOMETHING USEFUL IN 2 MINUTES</div>
                    <h2 className="v-tools__headline">Try VAULT before you register.</h2>
                    <div className="v-tools__grid">
                        {[
                            { to: "/tools/financial-organisation-checkup", title: "Check my financial organisation", time: "2 min", live: true },
                            { to: "/tools", title: "Audit my subscriptions",         time: "3 min", live: false },
                            { to: "/tools", title: "Understand my net worth",        time: "4 min", live: false },
                            { to: "/tools", title: "Review my financial documents",  time: "3 min", live: false },
                        ].map((t, i) => (
                            <Link key={i} to={t.to} className="v-tools__card" data-testid={`landing-tool-${i}`}>
                                <div className="v-tools__time">{t.time}{!t.live && " · SOON"}</div>
                                <div className="v-tools__title">{t.title}</div>
                                <span className="v-tools__arrow"><ArrowRight size={13}/></span>
                            </Link>
                        ))}
                    </div>
                    <Link to="/tools" className="v-btn v-btn--ghost mt-6" data-testid="landing-tools-explore">
                        Explore VAULT Tools <ArrowRight size={14}/>
                    </Link>
                </div>
            </section>

            {/* ═══════ 8. VAULT TOGETHER ═══════ */}
            <section className="v-together" data-testid="landing-together">
                <div className="v-container">
                    <div className="v-together__eyebrow">VAULT TOGETHER</div>
                    <h2 className="v-together__headline">
                        Mine. Yours. <span className="v-gold">Ours.</span>
                    </h2>
                    <p className="v-together__sub">
                        VAULT Together helps couples and families organise shared financial responsibilities
                        while keeping personal information private.
                    </p>
                    <div className="v-together__lenses">
                        {[
                            { tag: "MINE",     body: "Your private financial world.",           stripe: "linear-gradient(90deg,#22D3EE,#14B8A6)" },
                            { tag: "PARTNER",  body: "Only what your partner chooses to share.", stripe: "linear-gradient(90deg,#8B5CF6,#EC4899)" },
                            { tag: "JOINT",    body: "Things you own or owe together.",         stripe: "linear-gradient(90deg,#C6A86B,#F59E0B)" },
                            { tag: "TOGETHER", body: "A shared household view — by design.",     stripe: "linear-gradient(90deg,#10B981,#4F7CFF)" },
                        ].map((l) => (
                            <div key={l.tag} className="v-together__lens" data-testid={`landing-lens-${l.tag.toLowerCase()}`}>
                                <div className="v-together__stripe" style={{ background: l.stripe }} />
                                <div className="v-together__tag">{l.tag}</div>
                                <p className="v-together__body">{l.body}</p>
                            </div>
                        ))}
                    </div>
                    <div className="v-together__ctas">
                        <Link to="/together" className="v-btn v-btn--gold" data-testid="landing-together-cta">
                            Discover VAULT Together <ArrowRight size={14} />
                        </Link>
                        <Link to="/pricing" className="v-btn v-btn--ghost" data-testid="landing-family-plan-cta">
                            View Family plan
                        </Link>
                    </div>
                </div>
            </section>

            {/* ═══════ 4.9 HUMAN CLOSING CTA (cinematic) ═══════ */}
            <LifestyleVideo moment={getMoment("final-cta")} side="right" />

            {/* ═══════ 5. FINAL CTA ═══════ */}
            <section className="v-final">
                <div className="v-container text-center">
                    <h2 className="v-final-headline">
                        {t("final.headline_1")}<br /> {t("final.headline_2")}
                    </h2>
                    <div className="v-final-ctas">
                        <Link to="/signup" className="v-btn v-btn--gold v-btn--lg" data-testid="footer-cta">
                            {t("final.cta")} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer — shared PublicFooter with expanded groups & legal entity fields */}
            <PublicFooter />
        </div>
    );
}

function PreviewKpi({ label, value, delta, positive }) {
    return (
        <div className="v-kpi">
            <div className="v-kpi-label">{label}</div>
            <div className="v-kpi-value">{value}</div>
            <div className={`v-kpi-delta ${positive ? "v-kpi-delta--pos" : ""}`}>{delta}</div>
        </div>
    );
}

/* ─────────────── Testimonial section (3-up desktop / 1 mobile) ─────────────── */
function TestimonialCard({ t }) {
    const meta = [t.role, t.customer_type, t.city, t.country].filter(Boolean).join(" · ");
    return (
        <blockquote className="v-testimonial" data-testid={`testimonial-card-${t.id}`}>
            <p className="v-testimonial-quote">&ldquo;{t.quote}&rdquo;</p>
            <footer className="v-testimonial-foot">
                <span className="v-testimonial-author">{t.display_name}</span>
                <span className="v-testimonial-role">{meta}</span>
            </footer>
        </blockquote>
    );
}

function TestimonialSection({ testimonials, note }) {
    const [i, setI] = useState(0);
    const total = testimonials.length;
    const go = (delta) => setI((i + delta + total) % total);
    return (
        <div className="v-testimonials-wrap" data-testid="testimonials-section">
            <div className="v-testimonials-eyebrow">
                <span className="v-beta-dot" />
                Genuine · Verified · Global
            </div>
            <h2 className="v-testimonials-title">What VAULT users are saying.</h2>
            <p className="v-testimonials-sub">
                Real experiences from people organising their financial lives with VAULT.
            </p>

            {/* Desktop 3-up */}
            <div className="v-testimonials" data-testid="testimonials-desktop">
                {testimonials.slice(0, 3).map((t) => <TestimonialCard key={t.id} t={t} />)}
            </div>

            {/* Mobile single-item carousel */}
            <div className="v-testimonials-mobile" data-testid="testimonials-mobile">
                <div className="v-testimonials-mobile-track">
                    <TestimonialCard t={testimonials[i]} />
                </div>
                <div className="v-testimonials-nav">
                    <button className="v-testimonials-arrow" onClick={() => go(-1)}
                            data-testid="testimonials-prev" aria-label="Previous">&larr;</button>
                    {testimonials.slice(0, Math.min(6, total)).map((t, idx) => (
                        <button key={t.id}
                                className={`v-testimonials-dot ${idx === i ? "v-testimonials-dot--active" : ""}`}
                                onClick={() => setI(idx)}
                                data-testid={`testimonials-dot-${idx}`}
                                aria-label={`Show testimonial ${idx + 1}`} />
                    ))}
                    <button className="v-testimonials-arrow" onClick={() => go(1)}
                            data-testid="testimonials-next" aria-label="Next">&rarr;</button>
                </div>
            </div>

            <p className="v-testimonials-note" data-testid="testimonials-note">
                {note || "Only verified, approved and consented customers appear here. Display order is randomised on every visit."}
            </p>
        </div>
    );
}

function DashboardCanvas() {
    return (
        <div className="v-canvas-live" data-testid="hero-preview">
            <div className="v-canvas-frame">
                <div className="v-canvas-chrome">
                    <span className="v-canvas-dot" style={{ background: "#F87171" }} />
                    <span className="v-canvas-dot" style={{ background: "#FBBF24" }} />
                    <span className="v-canvas-dot" style={{ background: "#34D399" }} />
                    <div className="v-canvas-url">vault · dashboard</div>
                </div>
                <img
                    src="https://customer-assets-lxgj4vgw.emergentagent.net/job_finances-40cards/artifacts/kb0flkwt_ChatGPT%20Image%20Aug%2023%2C%202026%2C%2009_19_28%20PM.png"
                    alt="VAULT Command Centre — premium Midnight Luxe dashboard"
                    className="v-canvas-img"
                    loading="lazy"
                />
            </div>
        </div>
    );
}

/* ═════════════════════════════════════════════════════════════════════ */
const landingCSS = `
:root {
  --navy-950: #05080F;
  --navy-900: #0B1122;
  --navy-800: #101827;
  --navy-700: #14213D;
  --ivory: #F6F3EC;
  --ivory-dim: rgba(246,243,236,0.72);
  --ivory-faint: rgba(246,243,236,0.45);
  --gold: #C6A86B;
  --emerald: #29C58A;
  --sapphire: #4F7CFF;
  --border: rgba(246,243,236,0.08);
}
.v-landing { background: var(--navy-900); color: var(--ivory); font-family: "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; -webkit-font-smoothing: antialiased; min-height: 100vh; }
.v-container { max-width: 1200px; margin: 0 auto; padding-left: 28px; padding-right: 28px; }

/* Nav */
.v-nav { position: sticky; top: 0; z-index: 40; background: rgba(11,17,34,0.82); backdrop-filter: blur(12px); border-bottom: 1px solid transparent; transition: border-color .2s ease; }
.v-nav--scrolled { border-bottom-color: var(--border); }
.v-nav-link { font-size: 14px; color: var(--ivory-dim); font-weight: 500; transition: color .15s; }
.v-nav-link:hover { color: var(--ivory); }
.v-brand-word { font-weight: 900; font-size: 22px; letter-spacing: 0.22em; color: var(--ivory); }
.v-brand-word--sm { font-size: 18px; }
.v-brand-tag { font-weight: 600; font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; color: var(--ivory-faint); transform: translateY(-2px); }

/* Buttons */
.v-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; border-radius: 999px; font-size: 14px; font-weight: 600; transition: transform .15s ease, background .15s, border-color .15s; }
.v-btn--lg { padding: 14px 24px; font-size: 15px; }
.v-btn--primary { background: var(--ivory); color: var(--navy-900); }
.v-btn--primary:hover { transform: translateY(-1px); }
.v-btn--gold { background: var(--gold); color: var(--navy-900); box-shadow: 0 8px 24px -12px rgba(198,168,107,0.55); }
.v-btn--gold:hover { transform: translateY(-1px); background: #d3b978; }
.v-btn--ghost { background: transparent; color: var(--ivory); border: 1px solid var(--border); }
.v-btn--ghost:hover { background: rgba(246,243,236,0.04); border-color: rgba(246,243,236,0.2); }

/* Section 8 — VAULT Together */
.v-together { padding: 88px 0 72px; background: linear-gradient(180deg, var(--navy-900) 0%, var(--navy-950) 100%); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
.v-together__eyebrow { font-size: 11px; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--gold); margin-bottom: 16px; }
.v-together__headline { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(38px, 6vw, 72px); font-weight: 500; letter-spacing: -0.025em; line-height: 1.05; color: var(--ivory); margin: 0 0 20px; }
.v-together__sub { font-size: 16.5px; line-height: 1.6; color: var(--ivory-dim); max-width: 560px; margin: 0 0 44px; }
.v-together__lenses { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 40px; }
@media (max-width: 900px) { .v-together__lenses { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .v-together__lenses { grid-template-columns: 1fr; } }
.v-together__lens { position: relative; padding: 24px 22px 22px; background: rgba(246,243,236,0.03); border: 1px solid var(--border); border-radius: 18px; overflow: hidden; transition: transform .24s cubic-bezier(0.16,1,0.3,1), border-color .24s cubic-bezier(0.16,1,0.3,1); }
.v-together__lens:hover { transform: translateY(-4px); border-color: rgba(198,168,107,0.35); }
.v-together__stripe { position: absolute; top: 0; left: 0; right: 0; height: 3px; }
.v-together__tag { font-size: 10px; letter-spacing: 0.28em; font-weight: 800; color: var(--ivory-faint); margin-bottom: 10px; }
.v-together__body { font-size: 14px; line-height: 1.55; color: var(--ivory-dim); margin: 0; }
.v-together__ctas { display: flex; gap: 12px; flex-wrap: wrap; }

/* Section 7 — Useful Tools */
.v-tools { padding: 72px 0 24px; background: linear-gradient(180deg, var(--navy-800) 0%, var(--navy-900) 100%); border-top: 1px solid var(--border); }
.v-tools__eyebrow { font-size: 11px; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--gold); margin-bottom: 14px; }
.v-tools__headline { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(36px, 5vw, 52px); font-weight: 500; letter-spacing: -0.02em; line-height: 1.05; color: var(--ivory); margin: 0 0 36px; }
.v-tools__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
@media (max-width: 900px) { .v-tools__grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .v-tools__grid { grid-template-columns: 1fr; } }
.v-tools__card { position: relative; display: flex; flex-direction: column; padding: 22px 20px 60px; border-radius: 16px; background: rgba(246,243,236,0.03); border: 1px solid var(--border); text-decoration: none; transition: transform 220ms cubic-bezier(0.16,1,0.3,1), border-color 220ms cubic-bezier(0.16,1,0.3,1); }
.v-tools__card:hover { transform: translateY(-4px); border-color: rgba(198,168,107,0.35); }
.v-tools__time { font-size: 10px; letter-spacing: 0.24em; font-weight: 700; color: var(--gold); margin-bottom: 10px; }
.v-tools__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 20px; font-weight: 500; letter-spacing: -0.01em; line-height: 1.15; color: var(--ivory); }
.v-tools__arrow { position: absolute; bottom: 18px; right: 20px; color: var(--gold); }

/* Section 6.5 — "I want to…" journeys */
.v-wants { padding: 88px 0 24px; background: linear-gradient(180deg, #FBF7EE 0%, #F3EBD3 100%); color: #0F1E3A; border-top: 1px solid rgba(15,30,58,0.06); }
.v-wants__eyebrow { font-size: 11px; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 14px; }
.v-wants__headline { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(44px, 6vw, 72px); font-weight: 500; letter-spacing: -0.025em; line-height: 1.0; color: #0F1E3A; margin: 0 0 18px; }
.v-wants__sub { font-size: 16px; line-height: 1.6; color: #57534E; max-width: 560px; margin: 0 0 44px; }
.v-wants__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
@media (max-width: 1080px) { .v-wants__grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 560px)  { .v-wants__grid { grid-template-columns: 1fr; } }
.v-wants__card { position: relative; display: flex; flex-direction: column; padding: 22px 22px 20px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 20px; text-decoration: none; color: inherit; overflow: hidden; transition: transform 220ms cubic-bezier(0.16,1,0.3,1), box-shadow 220ms cubic-bezier(0.16,1,0.3,1), border-color 220ms cubic-bezier(0.16,1,0.3,1); }
.v-wants__card:hover { transform: translateY(-4px); box-shadow: 0 24px 48px -20px rgba(15,30,58,0.2); border-color: rgba(198,168,107,0.35); }
.v-wants__card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px; }
.v-wants__card--cobalt::before  { background: linear-gradient(90deg,#3B82F6,#60A5FA); }
.v-wants__card--coral::before   { background: linear-gradient(90deg,#F97316,#FB923C); }
.v-wants__card--emerald::before { background: linear-gradient(90deg,#10B981,#34D399); }
.v-wants__card--teal::before    { background: linear-gradient(90deg,#14B8A6,#5EEAD4); }
.v-wants__card--amber::before   { background: linear-gradient(90deg,#F59E0B,#FBBF24); }
.v-wants__card--violet::before  { background: linear-gradient(90deg,#8B5CF6,#A78BFA); }
.v-wants__card--gold::before    { background: linear-gradient(90deg,#C6A86B,#E6D5A8); }
.v-wants__card--aqua::before    { background: linear-gradient(90deg,#22D3EE,#67E8F9); }
.v-wants__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 22px; font-weight: 500; letter-spacing: -0.012em; line-height: 1.15; color: #0F1E3A; margin-bottom: 8px; }
.v-wants__body { font-size: 13.5px; line-height: 1.5; color: #57534E; margin: 0 0 14px; flex: 1; }
.v-wants__arrow { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 600; color: #0F1E3A; }

/* Hero — compressed to fit a single 1440×900 viewport */
.v-hero { padding-top: 56px; padding-bottom: 64px; }
@media (min-width: 768px) { .v-hero { padding-top: 64px; padding-bottom: 72px; } }
.v-hero-headline { font-size: 42px; line-height: 1.04; letter-spacing: -0.03em; font-weight: 800; color: var(--ivory); }
@media (min-width: 768px) { .v-hero-headline { font-size: 60px; } }
@media (min-width: 1280px) { .v-hero-headline { font-size: 68px; } }
.v-hero-sub { margin-top: 18px; max-width: 500px; font-size: 17px; line-height: 1.55; color: var(--ivory-dim); }
.v-hero-ctas { margin-top: 28px; display: flex; flex-wrap: wrap; gap: 12px; }
.v-gold { color: var(--gold); }

/* Preview mini card */
.v-preview { background: linear-gradient(180deg, rgba(20,33,61,0.9) 0%, rgba(11,17,34,0.9) 100%); border: 1px solid var(--border); border-radius: 22px; padding: 24px; box-shadow: 0 40px 80px -40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(246,243,236,0.04); }
.v-preview-head { display: flex; align-items: center; justify-content: space-between; }
.v-preview-eyebrow { font-size: 10px; letter-spacing: 0.24em; font-weight: 700; color: var(--ivory-faint); text-transform: uppercase; }
.v-preview-title { font-size: 20px; font-weight: 700; color: var(--ivory); margin-top: 4px; }

.v-kpi { padding: 14px 4px; }
.v-kpi-label { font-size: 10.5px; letter-spacing: 0.2em; font-weight: 700; color: var(--ivory-faint); text-transform: uppercase; }
.v-kpi-value { font-size: 26px; font-weight: 800; color: var(--ivory); margin-top: 8px; letter-spacing: -0.02em; }
.v-kpi-delta { font-size: 12px; color: var(--ivory-dim); margin-top: 6px; }
.v-kpi-delta--pos { color: var(--emerald); }

/* Product statement */
.v-statement { padding-top: 56px; padding-bottom: 64px; }
@media (min-width: 768px) { .v-statement { padding-top: 88px; padding-bottom: 100px; } }
.v-statement-line { font-size: 40px; font-weight: 800; line-height: 1.15; letter-spacing: -0.025em; color: var(--ivory); max-width: 900px; margin: 0 auto; }
@media (min-width: 768px) { .v-statement-line { font-size: 60px; } }

/* Canvas — the one big dashboard mock */
.v-canvas { background: linear-gradient(180deg, #14213D 0%, #0B1122 100%); border: 1px solid var(--border); border-radius: 28px; padding: 40px; box-shadow: 0 60px 120px -60px rgba(0,0,0,0.75), inset 0 1px 0 rgba(246,243,236,0.05); }
@media (min-width: 768px) { .v-canvas { padding: 56px; } }
.v-canvas-toprow { display: flex; align-items: flex-end; justify-content: space-between; padding-bottom: 24px; border-bottom: 1px solid var(--border); }
.v-canvas-eyebrow { font-size: 10.5px; letter-spacing: 0.22em; font-weight: 700; color: var(--ivory-faint); text-transform: uppercase; }
.v-canvas-title { font-size: 22px; font-weight: 700; color: var(--ivory); margin-top: 6px; letter-spacing: -0.01em; }
.v-canvas-live { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--emerald); padding: 5px 12px; border: 1px solid rgba(41,197,138,0.28); border-radius: 999px; font-weight: 700; }
.v-canvas-kpi { padding: 8px 0; }
.v-canvas-kpi-label { font-size: 10.5px; letter-spacing: 0.2em; font-weight: 700; color: var(--ivory-faint); text-transform: uppercase; }
.v-canvas-kpi-value { font-size: 32px; font-weight: 800; color: var(--ivory); margin-top: 10px; letter-spacing: -0.02em; }
.v-canvas-kpi-sub { font-size: 12px; color: var(--ivory-dim); margin-top: 6px; }
.v-canvas-col { padding: 6px 0; }
.v-canvas-figure { font-size: 22px; font-weight: 700; color: var(--ivory); margin-top: 10px; letter-spacing: -0.01em; }
.v-canvas-bar { margin-top: 12px; height: 4px; background: rgba(246,243,236,0.06); border-radius: 999px; overflow: hidden; }
.v-canvas-bar span { display: block; height: 100%; border-radius: 999px; }

/* Canvas — real product screenshot in a chrome frame */
.v-canvas-live { max-width: 1120px; margin: 0 auto; }
.v-canvas-frame { border-radius: 20px; overflow: hidden; border: 1px solid var(--border); background: #0B1122; box-shadow: 0 60px 120px -60px rgba(0,0,0,0.75), inset 0 1px 0 rgba(246,243,236,0.05); }
.v-canvas-chrome { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: rgba(255,255,255,0.03); border-bottom: 1px solid var(--border); }
.v-canvas-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.v-canvas-url { margin-left: 14px; font-size: 12px; color: var(--ivory-faint); letter-spacing: 0.08em; font-weight: 600; text-transform: lowercase; }
.v-canvas-img { display: block; width: 100%; height: auto; }

/* Testimonials — premium 3-up desktop / 1-at-a-time mobile carousel */
.v-brand-footer-wrap { margin-top: 96px; padding-top: 32px; border-top: 1px solid var(--border); }
.v-testimonials-wrap { margin-top: 96px; }
.v-testimonials-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.22em; color: var(--gold); text-transform: uppercase; }
.v-testimonials-title { margin: 16px 0 10px; font-size: 34px; font-weight: 800; color: var(--ivory); letter-spacing: -0.02em; line-height: 1.15; }
@media (min-width: 900px) { .v-testimonials-title { font-size: 42px; } }
.v-testimonials-sub { font-size: 14.5px; line-height: 1.6; color: var(--ivory-dim); max-width: 560px; }
.v-testimonials { margin-top: 42px; display: grid; grid-template-columns: 1fr; gap: 28px; }
@media (min-width: 900px) { .v-testimonials { grid-template-columns: repeat(3, 1fr); gap: 32px; } }
.v-testimonial { border-top: 1px solid var(--border); padding-top: 22px; }
.v-testimonial-quote { font-size: 15.5px; line-height: 1.65; color: var(--ivory); font-weight: 500; }
.v-testimonial-foot { display: flex; flex-direction: column; margin-top: 18px; }
.v-testimonial-author { font-size: 13px; font-weight: 700; color: var(--ivory); letter-spacing: -0.01em; }
.v-testimonial-role { font-size: 11.5px; color: var(--ivory-faint); margin-top: 2px; letter-spacing: 0.02em; }
.v-testimonial-flag { display: inline-block; margin-right: 5px; }
/* Mobile single-item carousel */
.v-testimonials-mobile { display: none; margin-top: 40px; }
@media (max-width: 899px) {
    .v-testimonials { display: none; }
    .v-testimonials-mobile { display: block; }
}
.v-testimonials-mobile-track { padding-top: 22px; border-top: 1px solid var(--border); }
.v-testimonials-nav { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 22px; }
.v-testimonials-dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(246,243,236,0.25); border: none; padding: 0; cursor: pointer; transition: background .2s; }
.v-testimonials-dot--active { background: var(--gold); width: 22px; border-radius: 3px; }
.v-testimonials-arrow { width: 34px; height: 34px; border-radius: 50%; background: rgba(246,243,236,0.05); border: 1px solid var(--border); color: var(--ivory-dim); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: color .2s, border-color .2s; }
.v-testimonials-arrow:hover { color: var(--ivory); border-color: rgba(246,243,236,0.25); }
.v-testimonials-note { margin-top: 32px; font-size: 11px; color: var(--ivory-faint); letter-spacing: 0.02em; }

/* Founding cohort card — replaces the old GLOBAL BETA PROGRAMME block */
.v-beta { margin-top: 72px; position: relative; overflow: hidden; border-radius: 28px;
    background: radial-gradient(140% 90% at 0% 0%, rgba(198,168,107,0.16) 0%, rgba(198,168,107,0.02) 40%, transparent 70%),
                linear-gradient(180deg, rgba(246,243,236,0.03) 0%, rgba(246,243,236,0.01) 100%);
    border: 1px solid rgba(246,243,236,0.09);
    box-shadow: 0 30px 80px -50px rgba(0,0,0,0.6), inset 0 1px 0 rgba(246,243,236,0.05); }
.v-beta::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(198,168,107,0.35) 50%, transparent 100%); }
.v-beta-grid { display: grid; grid-template-columns: 1fr; gap: 32px; padding: 40px 32px; align-items: center; }
@media (min-width: 900px) { .v-beta-grid { grid-template-columns: 1.5fr 1fr; gap: 56px; padding: 56px 64px; } }
.v-beta-copy { }
.v-beta-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 10.5px; font-weight: 700;
    letter-spacing: 0.22em; color: var(--gold); text-transform: uppercase; }
.v-beta-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--gold);
    box-shadow: 0 0 0 4px rgba(198,168,107,0.15); animation: v-beta-pulse 2.4s ease-in-out infinite; }
@keyframes v-beta-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
.v-beta-title { margin: 16px 0 10px; font-size: 34px; font-weight: 800; color: var(--ivory);
    letter-spacing: -0.02em; line-height: 1.1; }
@media (min-width: 900px) { .v-beta-title { font-size: 40px; } }
.v-beta-body { font-size: 14.5px; line-height: 1.65; color: var(--ivory-dim); max-width: 520px; }
.v-beta-perks { list-style: none; padding: 0; margin: 20px 0 0; display: grid; gap: 10px; }
.v-beta-perks li { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: var(--ivory); }
.v-beta-check { width: 14px; height: 14px; border-radius: 50%; background: rgba(41,197,138,0.15);
    position: relative; flex-shrink: 0; }
.v-beta-check::after { content: ""; position: absolute; left: 4px; top: 3px; width: 4px; height: 7px;
    border: solid var(--emerald); border-width: 0 1.5px 1.5px 0; transform: rotate(45deg); }
.v-beta-cta-col { display: flex; flex-direction: column; align-items: flex-start; gap: 14px; padding-top: 4px; }
@media (min-width: 900px) { .v-beta-cta-col { align-items: flex-end; padding-top: 0; padding-left: 24px;
    border-left: 1px solid rgba(246,243,236,0.08); align-self: stretch; justify-content: center; } }
.v-beta-badge { font-size: 10px; font-weight: 700; letter-spacing: 0.2em; padding: 6px 12px;
    border-radius: 999px; background: rgba(41,197,138,0.12); color: var(--emerald);
    border: 1px solid rgba(41,197,138,0.25); text-transform: uppercase; }
.v-beta-btn { display: inline-flex; align-items: center; gap: 10px; padding: 14px 26px; border-radius: 999px;
    background: var(--gold); color: #0B1122; font-size: 14.5px; font-weight: 700; letter-spacing: -0.005em;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    box-shadow: 0 12px 28px -12px rgba(198,168,107,0.6); cursor: pointer; }
.v-beta-btn:hover { background: #d3b978; transform: translateY(-1px); box-shadow: 0 18px 34px -12px rgba(198,168,107,0.7); }
.v-beta-fine { font-size: 11.5px; color: var(--ivory-faint); }
@media (min-width: 900px) { .v-beta-fine { text-align: right; } }

/* Sold-out state — rose-tinted waitlist mode */
.v-beta--sold { background: radial-gradient(140% 90% at 0% 0%, rgba(244,114,182,0.14) 0%, rgba(244,114,182,0.02) 40%, transparent 70%), linear-gradient(180deg, rgba(246,243,236,0.03) 0%, rgba(246,243,236,0.01) 100%); }
.v-beta--sold::before { background: linear-gradient(90deg, transparent 0%, rgba(244,114,182,0.35) 50%, transparent 100%); }
.v-beta-eyebrow--sold { color: #f472b6; }
.v-beta-eyebrow--sold .v-beta-dot { background: #f472b6; box-shadow: 0 0 0 4px rgba(244,114,182,0.15); }
.v-beta-badge--sold { background: rgba(244,114,182,0.12); color: #f472b6; border-color: rgba(244,114,182,0.25); }

/* Benefits */
.v-benefits { padding-top: 64px; padding-bottom: 80px; }
@media (min-width: 768px) { .v-benefits { padding-top: 96px; padding-bottom: 120px; } }
.v-benefit { padding-left: 4px; }
.v-benefit-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--gold); margin-bottom: 20px; }
.v-benefit-title { font-size: 26px; font-weight: 700; color: var(--ivory); letter-spacing: -0.01em; }
.v-benefit-body { margin-top: 12px; font-size: 15.5px; line-height: 1.55; color: var(--ivory-dim); max-width: 320px; }

/* Security */
.v-security { padding-top: 56px; padding-bottom: 80px; }
@media (min-width: 768px) { .v-security { padding-top: 88px; padding-bottom: 120px; } }
.v-security-badge { display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 999px; background: rgba(198,168,107,0.1); color: var(--gold); font-size: 10.5px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; border: 1px solid rgba(198,168,107,0.25); }
.v-security-headline { margin-top: 16px; font-size: 36px; font-weight: 800; color: var(--ivory); letter-spacing: -0.02em; line-height: 1.1; max-width: 440px; }
@media (min-width: 768px) { .v-security-headline { font-size: 48px; } }
.v-security-list { display: flex; flex-direction: column; gap: 2px; }
.v-security-item { display: flex; align-items: center; gap: 14px; padding: 16px 0; font-size: 16px; color: var(--ivory); border-bottom: 1px solid var(--border); }
.v-security-item:last-child { border-bottom: none; }
.v-security-check { width: 10px; height: 10px; border-radius: 50%; background: var(--emerald); flex-shrink: 0; box-shadow: 0 0 0 3px rgba(41,197,138,0.15); }

/* Final CTA */
.v-final { padding-top: 72px; padding-bottom: 88px; background: linear-gradient(180deg, var(--navy-900) 0%, var(--navy-950) 100%); }
@media (min-width: 768px) { .v-final { padding-top: 100px; padding-bottom: 132px; } }
.v-final-headline { font-size: 44px; font-weight: 800; color: var(--ivory); letter-spacing: -0.025em; line-height: 1.1; }
@media (min-width: 768px) { .v-final-headline { font-size: 72px; } }
.v-final-ctas { margin-top: 42px; display: flex; justify-content: center; }

/* Footer */
.v-footer { padding-top: 40px; padding-bottom: 40px; border-top: 1px solid var(--border); }
.v-footer-links a { color: var(--ivory-dim); transition: color .15s; }
.v-footer-links a:hover { color: var(--ivory); }
.v-footer-legal { margin-top: 20px; font-size: 12px; color: var(--ivory-faint); }
/* Hero refinements (Feb 2026 homepage revision) — eyebrow, data-source
   line, trial line + pricing teaser. Kept in a small block so the wider
   landingCSS constant remains unchanged. */
.v-hero-eyebrow {
    display: inline-block;
    font-size: 11px;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #c9a96b;
    padding: 6px 12px;
    border: 1px solid rgba(201, 169, 107, 0.35);
    border-radius: 999px;
    margin-bottom: 20px;
    background: rgba(201, 169, 107, 0.08);
}
.v-hero-source {
    margin-top: 14px;
    font-size: 13px;
    line-height: 1.55;
    color: rgba(246, 243, 236, 0.62);
    max-width: 560px;
    border-left: 2px solid rgba(201, 169, 107, 0.45);
    padding-left: 12px;
}
.v-hero-trial {
    margin-top: 18px;
    font-size: 12.5px;
    color: rgba(246, 243, 236, 0.7);
    letter-spacing: 0.02em;
}
.v-hero-demo-link {
    color: #c9a96b;
    text-decoration: none;
    border-bottom: 1px solid rgba(201, 169, 107, 0.5);
}
.v-hero-demo-link:hover { border-bottom-color: #e6c78a; color: #e6c78a; }
.v-hero-price { margin-top: 10px; font-size: 12.5px; color: rgba(246, 243, 236, 0.55); }
.v-hero-price-link { color: rgba(246, 243, 236, 0.8); text-decoration: none; border-bottom: 1px solid rgba(246, 243, 236, 0.2); }
.v-hero-price-link:hover { color: #c9a96b; border-bottom-color: #c9a96b; }

/* Founder Story — editorial, typography-led, warm ivory bg + midnight body */
.v-founder { background: var(--ivory); color: var(--navy-900); padding: 96px 24px; }
@media (min-width: 768px) { .v-founder { padding: 140px 24px; } }
.v-founder-inner { max-width: 720px; margin: 0 auto; }
.v-founder-eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; letter-spacing: 0.32em; text-transform: uppercase; color: #a0894a; margin-bottom: 28px; }
.v-founder-dot { width: 6px; height: 6px; border-radius: 50%; background: #c9a253; }
.v-founder-headline { font-family: 'Fraunces', 'Times New Roman', serif; font-weight: 500; font-size: 34px; line-height: 1.18; letter-spacing: -0.015em; color: var(--navy-900); margin: 0 0 40px; }
@media (min-width: 768px) { .v-founder-headline { font-size: 48px; line-height: 1.12; margin-bottom: 56px; } }
.v-founder-body { font-size: 17px; line-height: 1.72; color: #1c2b48; }
@media (min-width: 768px) { .v-founder-body { font-size: 18px; line-height: 1.78; } }
.v-founder-body p { margin: 0 0 20px; }
.v-founder-mark { font-family: 'Fraunces', serif; font-weight: 500; font-size: 20px; line-height: 1.5; color: var(--navy-900); }
@media (min-width: 768px) { .v-founder-mark { font-size: 22px; } }
.v-founder-pull { font-family: 'Fraunces', serif; font-style: italic; font-size: 22px; line-height: 1.4; color: #0b1e3f; border-left: 2px solid #c9a253; padding: 8px 0 8px 22px; margin: 32px 0; max-width: 560px; }
@media (min-width: 768px) { .v-founder-pull { font-size: 26px; margin: 44px 0; } }
.v-founder-close { font-family: 'Fraunces', serif; font-size: 26px; line-height: 1.3; color: var(--navy-900); margin-top: 48px; letter-spacing: -0.01em; }
@media (min-width: 768px) { .v-founder-close { font-size: 32px; margin-top: 64px; } }
.v-founder-close .v-gold { color: #a0894a; }
.v-founder-signoff { margin-top: 40px; }
.v-founder-signoff-name { font-family: 'Fraunces', serif; font-size: 20px; color: var(--navy-900); }
.v-founder-signoff-role { margin-top: 4px; font-size: 13px; letter-spacing: 0.02em; color: rgba(11,30,63,0.55); }
`;
